import '../css/app.css';
import 'primeicons/primeicons.css';
import PrimeVue from "primevue/config";
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import ToastService from 'primevue/toastservice';

import { createApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from '../../vendor/tightenco/ziggy';
import axios from 'axios';
import * as Sentry from '@sentry/vue'

const appName = import.meta.env.VITE_APP_NAME || 'Divam';


const MyPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{sky.50}',
            100: '{sky.100}',
            200: '{sky.200}',
            300: '{sky.300}',
            400: '{sky.400}',
            500: '{sky.500}',
            600: '{sky.600}',
            700: '{sky.700}',
            800: '{sky.800}',
            900: '{sky.900}',
            950: '{sky.950}'
        }
    }
});


createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        // Get token from session (through inertia middleware)
        const token = props.initialPage.props.auth?.token
        if (token) {
            // Add automatically the token in header for every axios request
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
        }

        const app = createApp({ render: () => h(App, props) });
            app.use(plugin)
            .use(ZiggyVue)
            .use(PrimeVue, {
                theme: {
                    preset: MyPreset
                }
            })
            .use(ToastService);

        // --- Sentry ---
        Sentry.init({
            app: app,
            dsn: import.meta.env.VITE_SENTRY_VUE_DSN,
            release: import.meta.env.VITE_SENTRY_RELEASE,
            environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
            integrations: [
                Sentry.browserTracingIntegration({

                }),
            ],
            tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
            sendDefaultPii: true
        })

        // Sentry for inertia
        let navSpan
        router.on('navigate', (event) => {
            if (navSpan) navSpan.end()
            navSpan = Sentry.startInactiveSpan({
                name: event.detail.page?.component ?? 'InertiaNavigate',
                op: 'navigation',
            })
            Sentry.getCurrentScope().setTag("inertia-info", navSpan)
        })
        router.on('success', () => { navSpan?.end(); navSpan = undefined })
        router.on('error', (e) => {
            Sentry.captureException(e?.detail?.error ?? new Error('Inertia navigation error'))
            navSpan?.end(); navSpan = undefined
        })

        // Axios -> breadcrumbs + capture if error
        axios.interceptors.response.use(
            r => r,
            error => {
                if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                    axios.post(route('logout')).finally(() => {
                        // Redirection vers login via Inertia
                        router.visit(route('login'))
                    })
                } else {
                    Sentry.addBreadcrumb({
                        category: 'http',
                        type: 'error',
                        data: {
                            url: error.config?.url,
                            method: error.config?.method,
                            status: error.response?.status,
                        },
                        level: 'error',
                    })
                    Sentry.captureException(error)
                }

                return Promise.reject(error)
            }
        )

        app.mount(el);
    },
    progress: {
        color: '#4B5563',
    },
});
